import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { Box, Button, FormControl, MenuItem, Stack } from '@mui/material';
import InputLabel from 'components/InputLabel';
import RoundedMultilineInput from 'components/RoundedMultilineInput';
import Icon from 'components/Icon';
import SettingControl from 'components/SettingControl';
import Select from 'components/Select';
import { Switch } from 'components/Switch';
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { restrictToParentElement } from '@dnd-kit/modifiers';

import { DynamicChoice } from 'features/polls/components/DynamicChoice';
import type { DragEndEvent } from '@dnd-kit/core/dist/types';
import type { PollType } from 'features/polls/types';
import { POLLS_INPUT_MAX_LENGTH } from 'features/polls/constants';

const CHOICES_MAX_LIMIT = 30;
const CHOICE_HEIGHT = 38;

// Calculate max height for scrollable choices container
const CHOICE_ITEM_SPACING = 24;
const MAX_VISIBLE_CHOICES = 4;
const CHOICE_WITH_SPACING = CHOICE_HEIGHT + CHOICE_ITEM_SPACING;
const CHOICES_CONTAINER_MAX_HEIGHT =
  CHOICE_WITH_SPACING * MAX_VISIBLE_CHOICES - CHOICE_ITEM_SPACING;

export interface PollFormValues {
  question: string;
  type: PollType;
  choices: {
    text: string;
  }[];
  anonymous: boolean;
}

const PollForm = () => {
  const { t } = useTranslation(['modals', 'polls']);

  const {
    control,
    watch,
    formState: { errors },
  } = useFormContext<PollFormValues>();

  const pollType = watch('type');
  const choices = watch('choices');
  const showChoices = pollType === 'single' || pollType === 'multiple';

  const { fields, append, remove, move } = useFieldArray({
    control,
    name: 'choices',
    rules: {
      required: showChoices,
      validate: (_, values) => !showChoices || values.choices.length > 1,
    },
  });

  const sensors = useSensors(
    useSensor(MouseSensor),
    useSensor(TouchSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const addChoice = () => {
    append({ text: '' });
  };

  const deleteChoice = (index: number) => {
    remove(index);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over === null || active.id === over.id) {
      return;
    }

    let oldIndex = -1;
    let newIndex = -1;

    fields.forEach((field, index) => {
      if (field.id === active.id) {
        oldIndex = index;
      } else if (field.id === over.id) {
        newIndex = index;
      }
    });

    if (oldIndex !== -1 && newIndex !== -1) {
      move(oldIndex, newIndex);
    }
  };

  return (
    <Stack spacing={6}>
      <FormControl fullWidth error={!!errors?.question}>
        <InputLabel htmlFor="poll-form-question">{t('polls:poll_form.question.label')}</InputLabel>
        <Controller
          name="question"
          control={control}
          rules={{
            required: true,
          }}
          render={({ field: { ref, ...field } }) => (
            <RoundedMultilineInput
              id="poll-form-question"
              autoFocus
              inputProps={{
                maxLength: POLLS_INPUT_MAX_LENGTH,
              }}
              {...field}
              inputRef={ref}
            />
          )}
        />
      </FormControl>
      <FormControl fullWidth error={!!errors?.type}>
        <Controller
          name="type"
          control={control}
          rules={{
            required: true,
          }}
          render={({ field: { ref, ...field } }) => (
            <Select
              {...field}
              inputRef={ref}
              inputProps={{
                'aria-label': t('polls:poll_form.type.aria_label'),
              }}
            >
              <MenuItem value="single">{t('polls:poll_form.type.single')}</MenuItem>
              <MenuItem value="multiple">{t('polls:poll_form.type.multiple')}</MenuItem>
              <MenuItem value="free">{t('polls:poll_form.type.free')}</MenuItem>
            </Select>
          )}
        />
      </FormControl>
      {showChoices && (
        <div>
          <InputLabel
            sx={{
              mb: 1.5,
            }}
          >
            {t('polls:poll_form.choices.label')}
          </InputLabel>
          <Box
            sx={{
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                maxHeight: CHOICES_CONTAINER_MAX_HEIGHT,
                overflowY: 'auto',
              }}
            >
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
                modifiers={[restrictToParentElement]}
              >
                <SortableContext items={fields} strategy={verticalListSortingStrategy}>
                  <Stack spacing={6}>
                    {fields.map((item, index) => (
                      <DynamicChoice
                        key={item.id}
                        id={item.id}
                        control={control}
                        index={index}
                        errors={errors}
                        deleteChoice={deleteChoice}
                      />
                    ))}
                  </Stack>
                </SortableContext>
              </DndContext>
            </Box>
          </Box>
          <Button
            sx={{
              mt: choices.length ? 6 : 0,
            }}
            startIcon={<Icon name="add" />}
            type="button"
            variant="outlined"
            onClick={addChoice}
            disabled={choices.length === CHOICES_MAX_LIMIT}
          >
            {t('polls:poll_form.choices.btn_add_choice')}
          </Button>
        </div>
      )}
      <SettingControl
        label={t('polls:poll_form.anonymous_poll.label')}
        control={
          <FormControl>
            <Controller
              name="anonymous"
              control={control}
              render={({ field: { value, ref, ...field } }) => (
                <Switch
                  {...field}
                  slotProps={{
                    input: {
                      ref,
                      'aria-label': value
                        ? t('polls:poll_form.anonymous_poll.switch_disable_aria_label')
                        : t('polls:poll_form.anonymous_poll.switch_enable_aria_label'),
                    },
                  }}
                  checked={value}
                />
              )}
            />
          </FormControl>
        }
      />
    </Stack>
  );
};

export default PollForm;
